/**
 * Custom Hooks for Notifications
 * Reusable hooks for common notification operations
 */

import { useState, useEffect, useCallback } from "react";
import { useNotifications } from "@/contexts/NotificationContext";
import type {
    Notification,
    NotificationCategory,
    NotificationQuery,
} from "@/types/notification";

/**
 * Hook to get filtered notifications
 */
export const useFilteredNotifications = (query: NotificationQuery) => {
    const { fetchNotifications, notifications, isLoading } = useNotifications();

    useEffect(() => {
        fetchNotifications(query);
    }, [JSON.stringify(query)]);

    return { notifications, isLoading };
};

/**
 * Hook to get unread notifications only
 */
export const useUnreadNotifications = () => {
    return useFilteredNotifications({ is_read: false });
};

/**
 * Hook to get notifications by category
 */
export const useNotificationsByCategory = (category: NotificationCategory) => {
    return useFilteredNotifications({ category });
};

/**
 * Hook for notification sound/visual effects
 */
export const useNotificationEffects = () => {
    const { notifications } = useNotifications();
    const [lastNotificationId, setLastNotificationId] = useState<string | null>(null);

    useEffect(() => {
        if (notifications.length === 0) return;

        const latestNotification = notifications[0];

        // Check if this is a new notification
        if (latestNotification.id !== lastNotificationId) {
            setLastNotificationId(latestNotification.id);

            // Play notification sound
            playNotificationSound();

            // Show browser notification if permission granted
            showBrowserNotification(latestNotification);
        }
    }, [notifications, lastNotificationId]);

    const playNotificationSound = () => {
        try {
            const audio = new Audio("/notification-sound.mp3");
            audio.volume = 0.5;
            audio.play().catch((error) => {
                console.error("Failed to play notification sound:", error);
            });
        } catch (error) {
            console.error("Failed to create audio:", error);
        }
    };

    const showBrowserNotification = (notification: Notification) => {
        if ("Notification" in window && Notification.permission === "granted") {
            new Notification(notification.title, {
                body: notification.message,
                icon: "/logo.png",
                badge: "/badge.png",
                tag: notification.id,
            });
        }
    };

    const requestNotificationPermission = async () => {
        if ("Notification" in window && Notification.permission === "default") {
            await Notification.requestPermission();
        }
    };

    return { requestNotificationPermission };
};

/**
 * Hook for notification badge count
 */
export const useNotificationBadge = () => {
    const { unreadCount } = useNotifications();

    const getBadgeText = useCallback(() => {
        if (unreadCount.total === 0) return "";
        if (unreadCount.total > 99) return "99+";
        return unreadCount.total.toString();
    }, [unreadCount.total]);

    const getCategoryBadge = useCallback((category: NotificationCategory) => {
        const count = unreadCount.by_category[category] || 0;
        if (count === 0) return "";
        if (count > 99) return "99+";
        return count.toString();
    }, [unreadCount.by_category]);

    return {
        total: unreadCount.total,
        badgeText: getBadgeText(),
        getCategoryBadge,
        hasUnread: unreadCount.total > 0,
    };
};

/**
 * Hook for notification actions with optimistic updates
 */
export const useNotificationActions = () => {
    const { markAsRead, markAsClicked, deleteNotification } = useNotifications();

    const handleMarkAsRead = useCallback(async (notificationId: string) => {
        try {
            await markAsRead(notificationId);
        } catch (error) {
            console.error("Failed to mark as read:", error);
            // Could show toast notification here
        }
    }, [markAsRead]);

    const handleMarkAsClicked = useCallback(async (notificationId: string) => {
        try {
            await markAsClicked(notificationId);
        } catch (error) {
            console.error("Failed to mark as clicked:", error);
        }
    }, [markAsClicked]);

    const handleDelete = useCallback(async (notificationId: string) => {
        try {
            await deleteNotification(notificationId);
        } catch (error) {
            console.error("Failed to delete notification:", error);
        }
    }, [deleteNotification]);

    return {
        markAsRead: handleMarkAsRead,
        markAsClicked: handleMarkAsClicked,
        deleteNotification: handleDelete,
    };
};

/**
 * Hook for WebSocket connection status
 */
export const useNotificationConnection = () => {
    const { isConnected } = useNotifications();
    const [connectionQuality, setConnectionQuality] = useState<"good" | "poor" | "offline">("offline");

    useEffect(() => {
        if (isConnected) {
            setConnectionQuality("good");
        } else {
            setConnectionQuality("offline");
        }
    }, [isConnected]);

    return {
        isConnected,
        connectionQuality,
        statusText: isConnected ? "Connected" : "Offline",
        statusColor: isConnected ? "green" : "red",
    };
};

/**
 * Hook for notification grouping
 */
export const useGroupedNotifications = () => {
    const { notifications } = useNotifications();

    const groupByCategory = useCallback(() => {
        return notifications.reduce((acc, notification) => {
            const category = notification.category;
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(notification);
            return acc;
        }, {} as Record<NotificationCategory, Notification[]>);
    }, [notifications]);

    const groupByDate = useCallback(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        return notifications.reduce((acc, notification) => {
            const notificationDate = new Date(notification.created_at);
            notificationDate.setHours(0, 0, 0, 0);

            let group: string;
            if (notificationDate.getTime() === today.getTime()) {
                group = "Today";
            } else if (notificationDate.getTime() === yesterday.getTime()) {
                group = "Yesterday";
            } else {
                group = "Older";
            }

            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(notification);
            return acc;
        }, {} as Record<string, Notification[]>);
    }, [notifications]);

    return {
        byCategory: groupByCategory(),
        byDate: groupByDate(),
    };
};
